import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useArticles } from "@/hooks/useCatalogue";
import PageIntro from "@/components/PageIntro";
import Reveal from "@/components/Reveal";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function Journal() {
  const { data: articles = [] } = useArticles();
  const [featured, ...rest] = articles;
  return (
    <div>
      <PageIntro
        eyebrow="Learn together"
        title="The Journal"
        description="Honest writing on skincare and formulation—no exaggerated claims, no fabricated results."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="journal-list">
        {articles.length === 0 ? (
          <div className="border border-forest/20 p-1">
            <div className="border border-forest/10 px-8 py-16 text-center">
              <p className="font-serif font-light text-3xl text-forest">Articles coming soon</p>
              <p className="mt-3 text-charcoal/70 max-w-md mx-auto font-light">Join the community to be the first to read our journal.</p>
              <Link to="/join" className="mt-8 inline-flex btn btn-primary">{BRAND.joinCta}</Link>
            </div>
          </div>
        ) : (
          <>
            <Reveal>
              <Link to={`/journal/${featured.slug}`} data-testid={`journal-featured-${featured.slug}`} className="group grid lg:grid-cols-12 gap-8 items-end border-b border-forest/15 pb-14">
                <div className="lg:col-span-8">
                  <p className="eyebrow">{featured.category}</p>
                  <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-forest leading-[0.98] text-balance group-hover:text-olive transition-colors">
                    {featured.title}
                  </h2>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-base text-charcoal/65 font-light leading-relaxed">{featured.excerpt}</p>
                  <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-charcoal/45">
                    {formatDate(featured.created_at)} · {featured.read_minutes} min read
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-forest link-underline">
                    Read <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
            <ol>
              {rest.map((a, i) => (
                <Reveal as="li" key={a.slug} delay={i * 0.1}>
                  <Link to={`/journal/${a.slug}`} data-testid={`journal-item-${a.slug}`} className="group grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr_16rem] gap-6 py-10 border-b border-forest/15">
                    <span className="numeral pt-2">0{i + 2}</span>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.22em] text-champagne">{a.category}</p>
                      <h3 className="mt-3 font-serif font-light text-3xl sm:text-4xl text-forest leading-tight group-hover:text-olive transition-colors">{a.title}</h3>
                      <p className="mt-3 text-sm sm:text-base text-charcoal/65 font-light max-w-2xl">{a.excerpt}</p>
                    </div>
                    <p className="col-span-2 sm:col-span-1 text-[11px] uppercase tracking-[0.22em] text-charcoal/45 sm:text-right sm:pt-2">
                      {formatDate(a.created_at)}<br />{a.read_minutes} min read
                    </p>
                  </Link>
                </Reveal>
              ))}
            </ol>
          </>
        )}
      </section>
    </div>
  );
}
